<template>
  <modal @closeModal="closeModal" class="boolean-search-query-info-modal">
    <div slot="header">
      <ul class="info-menu font-light text-base">
        <li>
          <a :class="{ active: activeTab === 'color_coding'}" v-on:click="() => setActiveTab('color_coding')">
            <i class="fas fa-palette text-sm"></i><span>Syntax Colour Codes</span>
          </a>
        </li>
        <li>
          <a :class="{ active: activeTab === 'rules'}" v-on:click="() => setActiveTab('rules')">
            <i class="fas fa-book-open text-sm"></i><span>Rules</span>
          </a>
        </li>
        <li>
          <a :class="{ active: activeTab === 'instructions'}" v-on:click="() => setActiveTab('instructions')">
            <i class="fas fa-question text-sm"></i><span>Help</span>
          </a>
        </li>
      </ul>

      <div class="header-actions">
        <span class="close fas fa-times" v-on:click="closeModal"></span>
      </div>
    </div>
    <div slot="body" class="main-container">
      <div v-if="activeTab === 'color_coding'">
        <h4 class="font-bold">Colouring Legend</h4>
        <table class="info-legend">
          <tr>
            <td>Boolean Operators:</td>
            <td><div class="flex"><div class="q-hl-blue operator-pill">AND</div><div class="q-hl-blue operator-pill">&&</div></div></td>
          </tr>
          <tr>
            <td>Boolean Operators:</td>
            <td><div class="flex"><div class="q-hl-orange operator-pill">OR</div><div class="q-hl-orange operator-pill">||</div></div></td>
          </tr>
          <tr>
            <td>Negation Boolean Operators:</td>
            <td><div class="flex"><div class="q-hl-red operator-pill">NOT</div><div class="q-hl-red operator-pill">AND NOT</div></div></td>
          </tr>
          <tr>
            <td>Grouping:</td>
            <td><div class="flex"><div class="q-hl-orange operator-pill">( )</div></div></td>
          </tr>
          <tr>
            <td>Re-use of a Search:</td>
            <td><div class="flex"><div class="q-hl-teal operator-pill">group:</div></div></td>
          </tr>
          <tr>
            <td><span class="font-semibold">*</span> Fields:</td>
            <td><div class="flex"><div class="q-hl-teal operator-pill">headline:</div><div class="q-hl-teal operator-pill">content:</div><div class="q-hl-teal operator-pill">provider_tags:</div></div></td>
          </tr>
          <tr>
            <td><span class="font-semibold">*</span> Multi Syntax Fields:</td>
            <td><div class="flex"><div class="q-hl-grey operator-pill">case:</div><div class="q-hl-grey operator-pill">contains:</div><div class="q-hl-grey operator-pill">contains_case:</div></div></td>
          </tr>
          <tr>
            <td><span class="font-semibold">*</span> Proximity Search:</td>
            <td><div class="flex"><div class="q-hl-operator operator-pill">NEAR/<span class="opaque">5</span></div><div class="q-hl-operator operator-pill">BINEAR/<span class="opaque">5</span></div><div class="q-hl-operator operator-pill">~<span class="opaque">5</span></div><div class="q-hl-operator operator-pill">LIST_OR:</div></div></td>
          </tr>
          <tr>
            <td><span class="font-semibold">*</span> Wildcard Search:</td>
            <td><div class="flex"><div class="q-hl-wildcard operator-pill">?</div><div class="q-hl-wildcard operator-pill">*</div></div></td>
          </tr>
        </table>

        <div class="note" style="padding-top: 10px;"><span class="font-semibold">*</span><span class="text-sm pl5">The following operators cannot be used in searches of type TVEyes and X (formerly Twitter)</span></div>
      </div>

      <div class="info-rules" v-if="activeTab === 'rules'">
        <h4 class="font-bold">Query Builder Rules</h4>
        <h5 class="font-extrabold text-uppercase pt20">Must Do</h5>
        <ol>
          <li>Opening and closing <span class="font-bold text-italic">bracket count must match</span>, i.e. every <span class="font-bold text-italic">bracket opened</span>, must therefore be <span class="font-bold text-italic">closed</span>.</li>
          <li>When a <span class="font-bold text-italic">quotation mark is opened</span>, it must therefore be <span class="font-bold text-italic">closed</span>, resulting in an <span class="font-bold text-italic">even number of quote count</span>.</li>
          <li>When using <span class="font-bold text-italic">proximity operators</span>, the <span class="font-bold text-italic">phrase / single term must be wrapped in quotes</span>.</li>
          <li>Wildcards <span class="font-bold text-italic">can only be used</span> in the <span class="font-bold text-italic">middle or end</span> of a phrase / single term <span class="font-bold text-italic">not in the beginning</span>.</li>
          <li>In <span class="font-bold text-italic">Asian</span> searches, words from <span class="font-bold text-italic">languages without whitespace separators</span> (e.g. Chinese, Japanese, Korean) and words containing <span class="font-bold text-italic">split characters</span> (e.g. hyphens, underscores, slashes, dots) must be enclosed in <span class="font-bold text-italic">double quotes</span>.</li>
        </ol>
        <h5 class="font-extrabold text-uppercase pt20">Must Not Do</h5>
        <ol>
          <li>Query <span class="font-bold text-italic">cannot start with</span> boolean operators <span class="font-bold text-italic">AND / && / OR / ||</span>.</li>
          <li>Query <span class="font-bold text-italic">cannot end with</span> boolean operators <span class="font-bold text-italic">AND / && / OR / || / NOT</span>.</li>
          <li>Query <span class="font-bold text-italic">cannot start with</span> a wildcard (<span class="font-bold text-italic">? *</span>).</li>
          <li>Query <span class="font-bold text-italic">cannot have consecutive</span> boolean operators <span class="font-bold text-italic">AND / && / OR / || / NOT</span>.</li>
          <li>Wildcards <span class="font-bold text-italic">cannot</span> be used <span class="font-bold text-italic">with proximity operators</span>.</li>
        </ol>
      </div>

      <div class="info-help" v-if="activeTab === 'instructions'">
        <h4 class="font-bold">Guidelines</h4>
        <h5 class="font-extrabold text-uppercase pt20">General Knowledge</h5>
        <ol>
          <li>
            A <span class="font-bold text-italic">space between keywords</span> that are not wrapped in quotes is considered as an <span class="font-bold text-italic">OR</span>
            except for X (formerly Twitter) search where a space is considered as an <span class="font-bold text-italic">AND</span>.
          </li>
          <li>To see additional <span class="font-bold text-italic">X (formerly Twitter) operators</span> visit: <span class="font-bold text-italic"><a target="_blank" href="https://developer.x.com/en/docs/twitter-api/tweets/filtered-stream/integrate/build-a-rule#list">https://developer.x.com/en/docs/twitter-api/tweets/filtered-stream/integrate/build-a-rule#list</a></span>.</li>
        </ol>
        <h5 class="font-bold text-uppercase pt20">Fields</h5>
        <p>Fields (<span class="font-bold text-italic">headline: / content: / provider_tags:</span>) support having quotes, parenthesis and also ranges.</p>
        <ol>
          <li><span class="font-bold text-italic">[field]:"text"</span> - field must contain the string as it is written.</li>
          <li><span class="font-bold text-italic">[field]:(text1 OR text2)</span> - field must contain one of the strings written.</li>
          <li><span class="font-bold text-italic">[field]:["aaaa" TO "cccc"]</span> - captures field that includes 'aaaa' and 'cccc'.</li>
          <li><span class="font-bold text-italic">headline:[2020 TO 2021]</span> - this is only for headline and will match articles published between year 2020 and 2021.</li>
          <li><span class="font-bold text-italic">[field]:{"aaaa" TO "cccc"}</span> - will only match what is in the middle, meaning: 'aaab'.</li>
        </ol>
        <h5 class="font-bold text-uppercase pt20">Proximity Search</h5>
        <p>There are different operators to capture proximity, all of them are used to match words that are found in the vicinity but used in different scenarios.</p>
        <ol>
          <li>
            <span class="font-bold text-italic">NEAR/</span> - Using this will give importance to the order of the words you give.
            Example: <span class="font-bold text-italic">"word1 word2" NEAR/5 "word3 word4"</span> - if "word1 word2" <span class="font-bold text-italic">is before</span> "word3 word4" and <span class="font-bold text-italic">separated by maximum 5 words</span> it will match
          </li>
          <li>
              <span class="font-bold text-italic">~</span> - You give a phrase with multiple words wrapped with quotes and it will match if these words are in proximity of each other in any order
              Example: <span class="font-bold text-italic">"word1 word2 word3"~1</span> will match <span class="font-bold text-italic">"word1 word2 x word3", "word1 x word2 word3" and also "word1 x word3 word2"</span>
          </li>
          <li>
          <span class="font-bold text-italic">BINEAR/</span> - This is like NEAR but it works in both direction.
          Example: <span class="font-bold text-italic">"word1 word2 word3" BINEAR/5 "word4 word5 word6"</span> - if "word1 word2 word3" <span class="font-bold text-italic"> is not far from  </span> "word4 word5 word6" and <span class="font-bold text-italic">separated by maximum 5 words</span> it will match so  <span class="font-bold text-italic">"word1 word2 word3 sep1 sep2 sep3 sep4 sep5 word4 word5 word6"</span> will match but also  <span class="font-bold text-italic">"word4 word5 word6 sep1 sep2 sep3 sep4 sep5 word1 word2 word3"</span>
          </li>
          <li>
            If you want to have a list of sentences instead of just one sentence for the <span class="font-bold text-italic">NEAR</span> and <span class="font-bold text-italic">BINEAR</span>, you can use <span class="font-bold text-italic">LIST_OR</span>
            Example: <span class="font-bold text-italic">LIST_OR:<span>{</span>{"my sentence 1","my sentence 2"}} BINEAR/10 "my sentence c" </span> you can use <span class="font-bold text-italic">LIST_OR</span> on both side of the <span class="font-bold text-italic">NEAR</span> and <span class="font-bold text-italic">BINEAR</span>.
          </li>
        </ol>
        <div>
        </div>
        <h5 class="font-bold text-uppercase pt20">Wildcard Operators</h5>
        <p>Wildcards are used to match part of the term. There are two wildcards that can help in the searches:</p>
        <ol>
          <li>
            <span class="font-bold text-italic">?</span> - will match terms substituting only one letter, example: <span class="font-bold text-italic">te?t</span> will match <span class="font-bold text-italic">"text" or "test"</span>
          </li>
          <li>
            <span class="font-bold text-italic">*</span> - used to match terms where there might be multiple letters which do not form part of the main term, example: <span class="font-bold text-italic">test*</span> will match <span class="font-bold text-italic">test, tests and tester</span>
          </li>
        </ol>
        <h5 class="font-bold text-uppercase pt20">Multi Syntax</h5>
        <p>Multi syntax follows all the above mentioned rules with some exceptions:</p>
        <ol>
          <li>When creating a <span class="font-bold text-italic">multi syntax space matters, "回望牛 车水"</span> is not the same as <span class="font-bold text-italic">"回望牛车水"</span>. Also will not match if there is a <span class="font-bold text-italic">carriage return</span> instead of space</li>
          <li>
            <span class="font-bold text-italic">Proximity search</span> distance works differently than non multi syntax, the space is also counted when checking in distance. Example: <span class="font-bold text-italic">"aaaa bbb"~5</span> means <span class="font-bold text-italic">separate by 5 words or space</span> now not just separated by 5 words. Same goes for <span class="font-bold text-italic">NEAR</span> and <span class="font-bold text-italic">BINEAR</span>
          </li>
          <li><span class="font-bold text-italic">case:"text"</span> - must compare string respecting the casing in which it's written.</li>
          <li><span class="font-bold text-italic">contains:"text"</span> - similar to <span class="font-bold text-italic">*</span> wildcard operator, but a full sentence can be supplied instead of just one word.</li>
          <li><span class="font-bold text-italic">contains_case:"text"</span> - same as <span class="font-bold text-italic">contains:</span> but respects casing.</li>
          <li>
            <span class="font-bold text-italic">Fields</span> and <span class="font-bold text-italic">Multi Syntax Fields</span> can be combined using <span class="font-bold text-italic">__</span>. Example: <span class="font-bold text-italic">case:(headline:"headLine22" OR "test22") OR case__headline:"other way to do headline with cAsE"</span>
          </li>
          <li>Note that <span class="font-bold text-italic">backsearch, is not yet compatible</span> with this new type of search. This concerns only the search, the tags will work correctly with this new syntax during a backsearch deployment for new articles added to the project.</li>
        </ol>
      </div>
    </div>
  </modal>
</template>

<script>
  //Components
  import modal from '../../modal';

  export default {
    name: 'info-modal',

    data() {
      return {
        activeTab: 'color_coding'
      }
    },

    methods: {
      closeModal(e) {
        if (e) {
          e.preventDefault();
          e.stopPropagation();
        }

        this.$emit('closeModal');
      },

      setActiveTab (tab) {
        if(this.activeTab !== tab) {
          this.activeTab = tab;
        }
      }
    },

    components: {
      modal
    }
  }
</script>
